// yourSources — the pure view-model of a searchable list of the workspace's
// uploaded sources: the New project box's "Your sources" panel (#312) and the
// assistant form's SourcePicker share it (one filter, one keyboard rule).
//
// Nothing here adds anything: "Matches what you wrote" only ORDERS the list
// by a plain rule (shared words, no model; principles I, 3, 8). The person
// adds a source by + or a drag, and the drop is parsed at the boundary
// (`sourceDropped`): anything that is not a known source is ignored.
//
// Ceilings (named, never a bare number at a call site):
// - SOURCE_SEARCH_MAX: the search text's length; longer text is cut.
// - YOUR_SOURCES_PAGE: rows rendered per page in "All sources" (a huge
//   library is paged, never rendered whole).
// - YOUR_SOURCES_MATCHES_MAX: rows in "Matches what you wrote".
// - BOX_FILES_MAX: files one New project box takes at a time.
// - FILES_FOLDED: file lines the box's Files list shows before "{n} more".
// - FILES_IN_VIEW: file lines the open list grows to before it scrolls
//   inside (`{files in view}`), so the box never grows past the screen.

export type { SourceKind, YourSource, YourSourcesState };
export {
    BOX_FILES_MAX,
    FILES_FOLDED,
    FILES_IN_VIEW,
    fileListKey,
    filterSources,
    foldFiles,
    groupYourSources,
    listKey,
    SOURCE_SEARCH_MAX,
    sourceDropped,
    sourceKindOf,
    wordsOf,
    YOUR_SOURCES_MATCHES_MAX,
    YOUR_SOURCES_PAGE,
};

import * as Core from "@bilda/core";
import { exhaustSwitchCase } from "@bilda/utils";
import { parseResourceDrag } from "../components/Composer/PowerComposer/resourceDrag.js";
import { fileKindOf } from "../components/KnowledgeBank/knowledgeBankModel.js";

const SOURCE_SEARCH_MAX = 100;
const YOUR_SOURCES_PAGE = 50;
const YOUR_SOURCES_MATCHES_MAX = 5;
// The same ceiling as the sources one create may name.
const BOX_FILES_MAX = Core.Limits.SOURCES_PER_CREATE_MAX;
const FILES_FOLDED = 3;
const FILES_IN_VIEW = 6;

// The least a searchable source carries.
type SourceOption = { readonly id: string; readonly title: string };

// What a row shows for its kind. Read from the file name's extension (the
// only kind fact the library projects today); anything else is `unknown`:
// the generic file icon and no meta line.
type SourceKind = "document" | "presentation" | "image" | "unknown";

type YourSource = SourceOption & {
    readonly kind: SourceKind;
    // What the source is about, when the library carries it.
    readonly summary?: string;
};

// The panel's list as the host has it.
type YourSourcesState =
    | { readonly type: "loading" }
    | { readonly type: "failed" }
    | { readonly type: "ready"; readonly sources: readonly YourSource[] };

const sourceKindOf = (filename: string): SourceKind => {
    const kind = fileKindOf(filename);
    switch (kind) {
        case "pdf":
        case "word":
        case "text":
            return "document";
        case "presentation":
            return "presentation";
        case "image":
            return "image";
        case "audio":
        case "video":
        case "unknown":
            return "unknown";
        default:
            return exhaustSwitchCase(kind);
    }
};

// Case- and accent-insensitive form ("Källor" → "kallor").
const foldText = (s: string): string =>
    s.normalize("NFD").replace(/\p{M}/gu, "").toLocaleLowerCase();

// The sources whose title holds the query (folded). The query is cut at
// SOURCE_SEARCH_MAX first. O(n·t) time in the sources × title length.
const filterSources = <T extends SourceOption>(
    sources: readonly T[],
    query: string,
): readonly T[] => {
    const q = foldText(query.slice(0, SOURCE_SEARCH_MAX).trim());
    return q === ""
        ? sources
        : sources.filter((s) => foldText(s.title).includes(q));
};

// A tiny stop list, English and Swedish: words that say nothing about a topic.
const STOPWORDS: ReadonlySet<string> = new Set([
    "and",
    "the",
    "for",
    "with",
    "about",
    "from",
    "into",
    "that",
    "this",
    "what",
    "who",
    "should",
    "will",
    "course",
    "och",
    "att",
    "det",
    "som",
    "med",
    "till",
    "fran",
    "kurs",
    "ska",
    "den",
    "har",
    "pdf",
    "docx",
    "pptx",
]);

// The words of a text that can carry a topic: folded, at least three
// characters, not a stop word. O(n) in the text's length.
const wordsOf = (text: string): ReadonlySet<string> =>
    new Set(
        foldText(text)
            .split(/[^\p{L}\p{N}]+/u)
            .filter((w) => w.length >= 3 && !STOPWORDS.has(w)),
    );

type GroupedSources = {
    // Sources that share words with what was written, most shared first.
    readonly matches: readonly YourSource[];
    // The rest, in the library's order, cut at `shown`.
    readonly all: readonly YourSource[];
    // How many of the rest are left out (a "Show more" away).
    readonly more: number;
};

// The panel's two groups. `written` is the Name and the description; the
// guess looks at a source's title and its summary when it has one. With a
// query, the list is filtered first. O(n·w) time in the sources × the words
// of a title and summary; `shown` caps what is rendered.
const groupYourSources = (
    sources: readonly YourSource[],
    query: string,
    written: string,
    shown: number,
): GroupedSources => {
    const filtered = filterSources(sources, query);
    const wanted = wordsOf(written);
    const shared = (s: YourSource): number =>
        wanted.size === 0
            ? 0
            : [...wordsOf(`${s.title} ${s.summary ?? ""}`)].filter((w) =>
                  wanted.has(w),
              ).length;
    const matches = filtered
        .map((s, i) => ({ s, i, n: shared(s) }))
        .filter((m) => m.n > 0)
        .sort((a, b) => b.n - a.n || a.i - b.i)
        .slice(0, YOUR_SOURCES_MATCHES_MAX)
        .map((m) => m.s);
    const matched = new Set(matches.map((s) => s.id));
    const rest = filtered.filter((s) => !matched.has(s.id));
    const cut = Math.max(0, shown);
    return {
        matches,
        all: rest.slice(0, cut),
        more: Math.max(0, rest.length - cut),
    };
};

// A drop's payload → the id of a known source to add, or null: an OS file, a
// material, a malformed or unknown payload is ignored without a word.
const sourceDropped = (
    raw: string,
    known: readonly SourceOption[],
): string | null => {
    const ref = parseResourceDrag(raw);
    if (ref === null || ref.kind !== "source") return null;
    return known.some((s) => s.id === ref.id) ? ref.id : null;
};

// The combobox's keys over a list of `count` rows (the input keeps focus and
// points at the active row): arrows move, Enter toggles the active row.
// `active` -1 is no row yet: ArrowDown goes to the first, Enter does nothing.
type ListKeyAction =
    | { readonly type: "move"; readonly index: number }
    | { readonly type: "toggle"; readonly index: number }
    | { readonly type: "none" };

const listKey = (key: string, active: number, count: number): ListKeyAction => {
    const last = count - 1;
    const at = Math.max(-1, Math.min(active, last));
    switch (key) {
        case "ArrowDown":
            return count === 0
                ? { type: "none" }
                : { type: "move", index: Math.min(at + 1, last) };
        case "ArrowUp":
            return count === 0
                ? { type: "none" }
                : { type: "move", index: Math.max(at - 1, 0) };
        case "Enter":
            return at < 0 ? { type: "none" } : { type: "toggle", index: at };
        default:
            return { type: "none" };
    }
};

// The Files list as shown: folded, the first FILES_FOLDED and how many wait
// behind "{n} more"; open, all of them (the list scrolls past FILES_IN_VIEW).
// `foldable`: there is anything to fold at all. O(n).
const foldFiles = <T>(
    files: readonly T[],
    open: boolean,
): {
    readonly shown: readonly T[];
    readonly more: number;
    readonly foldable: boolean;
} => {
    const foldable = files.length > FILES_FOLDED;
    const shown = open || !foldable ? files : files.slice(0, FILES_FOLDED);
    return { shown, more: files.length - shown.length, foldable };
};

// The Files list's keys: the list is one tab stop; arrows (and Home/End)
// move between lines, Delete or Backspace takes the focused file out.
type FileListKeyAction =
    | { readonly type: "move"; readonly index: number }
    | { readonly type: "remove"; readonly index: number }
    | { readonly type: "none" };

const fileListKey = (
    key: string,
    at: number,
    count: number,
): FileListKeyAction => {
    if (count === 0) return { type: "none" };
    const last = count - 1;
    const here = Math.max(0, Math.min(at, last));
    switch (key) {
        case "ArrowDown":
            return { type: "move", index: Math.min(here + 1, last) };
        case "ArrowUp":
            return { type: "move", index: Math.max(here - 1, 0) };
        case "Home":
            return { type: "move", index: 0 };
        case "End":
            return { type: "move", index: last };
        case "Delete":
        case "Backspace":
            return { type: "remove", index: here };
        default:
            return { type: "none" };
    }
};
